import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Button,
  Chip,
  Divider,
  Spinner,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Select,
  SelectItem,
  Textarea,
} from "@heroui/react";
import {
  FileText,
  MagicWand,
  Database,
  Download,
  Eye,
  Calendar,
  CheckCircle,
} from "@phosphor-icons/react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { useSession } from "@clerk/clerk-react";
import { API_URL } from "constants/index";
import { toast } from "react-toastify";
import { useLocation } from "react-router-dom";
import EntitySearchBar from "./components/EntitySearchBar";
import ImagePicker from "./components/ImagePicker";
import ImageTablePicker from "./components/ImageTablePicker";

interface DocumentTemplate {
  id: string;
  name: string;
  description?: string;
  category: string;
  sharePointFileId: string;
  placeholders: string[];
  requiredContext: string[];
  createdAt: string;
  updatedAt: string;
}

interface ExtractedPlaceholder {
  placeholder: string;
  entity: string;
  property: string;
  entityNumber?: number | null; // e.g., 1 from "contact1.firstName"
  fullEntity?: string; // e.g., "contact1" from "contact1.firstName"
  relationPath?: string[]; // e.g., ["building"] from "unit.building.address"
  isValid: boolean;
  errorMessage?: string;
  isDynamicValue?: boolean; // Added for waarde.* placeholders
  dynamicValueKey?: string; // Added for the key part (e.g., 'datumVandaag')
}

interface LoopDynamicField {
  loopVariable: string;
  collectionPath: string;
  images: string[];
  dynamicValues: string[];
}

interface ExpandedLoopItem {
  index: number;
  itemLabel: string;
  itemData: any;
  images: string[];
  dynamicValues: string[];
}

interface ExpandedLoop {
  loopVariable: string;
  collectionPath: string;
  items: ExpandedLoopItem[];
}

const DocumentGenerator: React.FC = () => {
  const { session } = useSession();
  const location = useLocation();
  const [templates, setTemplates] = useState<DocumentTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] =
    useState<DocumentTemplate | null>(null);
  const [templatePlaceholders, setTemplatePlaceholders] = useState<
    ExtractedPlaceholder[]
  >([]);
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [selectedEntities, setSelectedEntities] = useState<Record<string, any>>(
    {},
  );
  const [entitySearchLoading, setEntitySearchLoading] = useState<
    Record<string, boolean>
  >({});
  const [dynamicValues, setDynamicValues] = useState<Record<string, string>>(
    {},
  );
  const [prefilledContext, setPrefilledContext] = useState<{
    entities?: Record<string, any>;
    dynamicValues?: Record<string, string>;
    category?: string;
  } | null>(null);
  const [imagePlaceholders, setImagePlaceholders] = useState<string[]>([]);
  const [imageSelections, setImageSelections] = useState<
    Record<string, string>
  >({});
  const [imageTableSelections, setImageTableSelections] = useState<string[]>(
    [],
  );
  const [loopDynamicFields, setLoopDynamicFields] = useState<
    LoopDynamicField[]
  >([]);
  const [expandedLoops, setExpandedLoops] = useState<ExpandedLoop[]>([]);
  const [loopDynamicValues, setLoopDynamicValues] = useState<
    Record<string, string>
  >({});

  // Fetch templates on component mount
  useEffect(() => {
    if (session) {
      fetchTemplates();
    }
  }, [session]);

  // Initialize from URL state (prefilled context)
  useEffect(() => {
    if (location.state) {
      const { entities, dynamicValues, category } = location.state as any;

      if (entities || dynamicValues || category) {
        console.log("📋 Prefilled context received:", { entities, dynamicValues, category });

        setPrefilledContext({
          entities: entities || {},
          dynamicValues: dynamicValues || {},
          category: category || undefined,
        });

        // Set category filter if provided
        if (category) {
          setSelectedCategory(category);
          toast.info(`Gefilterd op categorie: ${getCategoryLabel(category)}`);
        }

        // Set entities if provided
        if (entities && Object.keys(entities).length > 0) {
          setSelectedEntities(entities);
          console.log("✅ Pre-selected entities:", Object.keys(entities));
        }

        // Set dynamic values if provided
        if (dynamicValues && Object.keys(dynamicValues).length > 0) {
          setDynamicValues(dynamicValues);
          console.log("✅ Pre-filled dynamic values:", Object.keys(dynamicValues));
        }
      }
    }
  }, [location.state]);

  // Trigger loop expansion when entities are selected
  useEffect(() => {
    const expandLoopFields = async () => {
      console.log("🔍 Loop expansion effect triggered");
      console.log("  - loopDynamicFields:", loopDynamicFields);
      console.log("  - selectedTemplate:", selectedTemplate?.id);
      console.log("  - selectedEntities keys:", Object.keys(selectedEntities));

      // Check if we have loop dynamic fields
      if (loopDynamicFields.length === 0 || !selectedTemplate) {
        console.log("  ⏭️ Skipping: No loop dynamic fields or no template");
        return;
      }

      // Check if we have any entities selected
      if (Object.keys(selectedEntities).length === 0) {
        console.log("  ⏭️ Skipping: No entities selected");
        return;
      }

      // Check if all required entities for loops are selected
      const requiredEntities = loopDynamicFields.map(
        (loop) => loop.collectionPath.split(".")[0],
      );
      console.log("  - Required entities for loops:", requiredEntities);
      console.log("  - Checking selectedEntities:", selectedEntities);

      const hasAllRequiredEntities = requiredEntities.every((entity) => {
        const hasEntity = selectedEntities[entity] !== undefined;
        console.log(`    - Has ${entity}? ${hasEntity}`);
        return hasEntity;
      });

      if (!hasAllRequiredEntities) {
        console.log(
          "  ⏭️ Not all required entities selected for loop expansion yet",
        );
        return;
      }

      try {
        console.log("🔁 Expanding loop fields...");

        const response = await fetchWithAuth(
          `${API_URL}/document-generation/templates/expand-loop-fields`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              templateId: parseInt(selectedTemplate.id),
              context: selectedEntities,
            }),
          },
          session,
        );

        if (response.ok) {
          const data = await response.json();
          console.log("✅ Loop fields expanded:", data.expandedFields);
          setExpandedLoops(data.expandedFields || []);

          // Show toast notification
          const totalItems = data.expandedFields.reduce(
            (sum: number, loop: ExpandedLoop) => sum + loop.items.length,
            0,
          );
          toast.success(`Loop velden uitgebreid: ${totalItems} items gevonden`);
        } else {
          console.error("Failed to expand loop fields");
        }
      } catch (error) {
        console.error("Error expanding loop fields:", error);
        toast.error("Kon loop velden niet uitbreiden");
      }
    };

    expandLoopFields();
  }, [loopDynamicFields, selectedEntities, selectedTemplate, session]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await fetchWithAuth(
        `${API_URL}/document-generation/templates`,
        { method: "GET" },
        session,
      );

      if (response.ok) {
        const data = await response.json();
        setTemplates(data);
      } else {
        throw new Error("Failed to fetch templates");
      }
    } catch (error) {
      console.error("Error fetching templates:", error);
      toast.error("Kon sjablonen niet laden");
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateSelect = async (template: DocumentTemplate) => {
    setSelectedTemplate(template);

    // Reset selected entities, dynamic values, and images when changing templates
    // BUT preserve prefilled context from navigation
    if (prefilledContext) {
      // Keep prefilled entities and dynamic values
      console.log("🔒 Preserving prefilled context during template selection");
      setSelectedEntities(prefilledContext.entities || {});
      setDynamicValues(prefilledContext.dynamicValues || {});
    } else {
      // Only reset if there's no prefilled context
      setSelectedEntities({});
      setDynamicValues({});
    }

    setImagePlaceholders([]);
    setImageSelections({});
    setLoopDynamicFields([]);
    setExpandedLoops([]);
    setLoopDynamicValues({});

    // Analyze template placeholders
    try {
      const response = await fetchWithAuth(
        `${API_URL}/document-generation/templates/analyze/${template.id}`,
        { method: "POST" },
        session,
      );

      if (response.ok) {
        const data = await response.json();
        console.log("Template analysis result:", data);

        // The backend returns placeholders in the 'placeholders' array
        setTemplatePlaceholders(data.placeholders || []);

        // Set default values for known dynamic value placeholders
        const dynamicPlaceholders = (data.placeholders || []).filter(
          (p: ExtractedPlaceholder) => p.isDynamicValue && p.isValid
        );
        const defaultDynamicValues: Record<string, string> = {};

        dynamicPlaceholders.forEach((placeholder: ExtractedPlaceholder) => {
          if (placeholder.dynamicValueKey === 'datumVandaag') {
            // Format current date as DD/MM/YYYY
            const today = new Date();
            const day = today.getDate().toString().padStart(2, '0');
            const month = (today.getMonth() + 1).toString().padStart(2, '0');
            const year = today.getFullYear();
            defaultDynamicValues['datumVandaag'] = `${day}/${month}/${year}`;
          }
        });

        // Only set defaults if not already prefilled
        if (Object.keys(defaultDynamicValues).length > 0) {
          setDynamicValues((prev) => ({
            ...defaultDynamicValues,
            ...prev, // Prefilled values take precedence
          }));
          console.log("✅ Set default dynamic values:", defaultDynamicValues);
        }

        // Extract image placeholders if any
        if (data.imagePlaceholders && data.imagePlaceholders.length > 0) {
          setImagePlaceholders(data.imagePlaceholders);
          console.log("Image placeholders found:", data.imagePlaceholders);
        }

        // Extract loop dynamic fields if any
        if (data.loopDynamicFields && data.loopDynamicFields.length > 0) {
          setLoopDynamicFields(data.loopDynamicFields);
          console.log("Loop dynamic fields found:", data.loopDynamicFields);
        }

        // Show success message with analysis stats
        if (data.analysisStats) {
          const stats = data.analysisStats;
          const imageCount = data.imagePlaceholders?.length || 0;
          const message =
            imageCount > 0
              ? `Sjabloon geanalyseerd: ${stats.validCount} geldige, ${stats.invalidCount} ongeldige placeholders en ${imageCount} afbeelding(en) gevonden`
              : `Sjabloon geanalyseerd: ${stats.validCount} geldige, ${stats.invalidCount} ongeldige placeholders gevonden`;
          toast.success(message);
        }
      } else {
        throw new Error("Failed to analyze template");
      }
    } catch (error) {
      console.error("Error analyzing template:", error);
      toast.error("Kon sjabloon niet analyseren");
    }
  };

  const getDynamicValuePlaceholders = (): ExtractedPlaceholder[] => {
    const allDynamicPlaceholders = templatePlaceholders.filter((p) => p.isDynamicValue && p.isValid);

    // Deduplicate based on dynamicValueKey to prevent showing the same field multiple times
    // If {waarde.datumVandaag} appears 3 times in the document, only show it once
    const uniquePlaceholders = new Map<string, ExtractedPlaceholder>();
    allDynamicPlaceholders.forEach((placeholder) => {
      if (placeholder.dynamicValueKey && !uniquePlaceholders.has(placeholder.dynamicValueKey)) {
        uniquePlaceholders.set(placeholder.dynamicValueKey, placeholder);
      }
    });

    return Array.from(uniquePlaceholders.values());
  };

  const handleDynamicValueChange = (key: string, value: string) => {
    setDynamicValues((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleImageSelect = (imageName: string, sharePointFileId: string) => {
    console.log(`Image selected for ${imageName}:`, sharePointFileId);
    setImageSelections((prev) => ({
      ...prev,
      [imageName]: sharePointFileId,
    }));
  };

  const getRequiredEntityInstances = (): Array<{
    baseEntity: string;
    fullEntity: string;
    entityNumber: number | null;
    displayName: string;
  }> => {
    const entityInstances = new Map<
      string,
      {
        baseEntity: string;
        fullEntity: string;
        entityNumber: number | null;
        displayName: string;
      }
    >();

    templatePlaceholders.forEach((placeholder) => {
      if (placeholder.isValid && placeholder.entity && placeholder.fullEntity) {
        const key = placeholder.fullEntity;
        if (!entityInstances.has(key)) {
          const displayName = placeholder.entityNumber
            ? `${placeholder.entity} #${placeholder.entityNumber}`
            : placeholder.entity;

          entityInstances.set(key, {
            baseEntity: placeholder.entity,
            fullEntity: placeholder.fullEntity,
            entityNumber: placeholder.entityNumber ?? null,
            displayName,
          });
        }
      }
    });

    const instances = Array.from(entityInstances.values()).sort((a, b) => {
      // Sort by base entity first, then by number
      if (a.baseEntity !== b.baseEntity) {
        return a.baseEntity.localeCompare(b.baseEntity);
      }
      return (a.entityNumber ?? 0) - (b.entityNumber ?? 0);
    });

    // Debug logging to check entity matching
    console.log("📊 Entity instances required by template:", instances.map(i => i.fullEntity));
    console.log("📊 Available prefilled entities:", Object.keys(selectedEntities));
    instances.forEach(instance => {
      const hasEntity = selectedEntities[instance.fullEntity];
      console.log(`  ${instance.fullEntity}: ${hasEntity ? '✅ FOUND' : '❌ NOT FOUND'}`);
      if (hasEntity) {
        console.log(`    -> Name:`, hasEntity.name || hasEntity.id);
      }
    });

    return instances;
  };

  const handleEntitySelect = (entityInstance: string, selectedEntity: any) => {
    console.log("Entity selected for", entityInstance, ":", selectedEntity);
    setSelectedEntities((prev) => ({
      ...prev,
      [entityInstance]: selectedEntity,
    }));
  };

  const handleEntityClear = (entityInstance: string) => {
    console.log("Entity cleared for", entityInstance);
    setSelectedEntities((prev) => {
      const updated = { ...prev };
      delete updated[entityInstance];
      return updated;
    });
  };

  /**
   * Derive the buildingId / unitId / acquisitionId the ImagePicker should filter
   * on for a loop iteration. The iteration's entity is already determined (e.g.
   * the Nth unit of `building.units`), so we must not fall back to the
   * top-level selectedEntities — that would show the wrong unit's pictures.
   */
  const getLoopItemImagePickerContext = (
    loop: ExpandedLoop,
    item: ExpandedLoopItem,
  ): {
    buildingId?: string;
    unitId?: string;
    acquisitionId?: string;
    isLoopScoped: boolean;
  } => {
    const itemData = item.itemData || {};
    const itemId = itemData.resourceId || itemData.id;
    const parentBuildingId =
      itemData.buildingId ||
      itemData.building?.id ||
      itemData.building?.resourceId;

    const path = (loop.collectionPath || "").toLowerCase();

    // Units collection → item is a unit; also propagate parent building if known
    if (path.endsWith("units") || path.endsWith(".unit")) {
      return {
        buildingId:
          parentBuildingId ||
          selectedEntities["building"]?.resourceId ||
          selectedEntities["building"]?.id,
        unitId: itemId,
        acquisitionId:
          selectedEntities["acquisition"]?.resourceId ||
          selectedEntities["acquisition"]?.id,
        isLoopScoped: Boolean(itemId),
      };
    }

    // Buildings collection → item is a building
    if (path.endsWith("buildings") || path.endsWith(".building")) {
      return {
        buildingId: itemId,
        unitId: undefined,
        acquisitionId:
          selectedEntities["acquisition"]?.resourceId ||
          selectedEntities["acquisition"]?.id,
        isLoopScoped: Boolean(itemId),
      };
    }

    // Acquisitions collection → item is an acquisition
    if (path.endsWith("acquisitions") || path.endsWith(".acquisition")) {
      return {
        buildingId:
          selectedEntities["building"]?.resourceId ||
          selectedEntities["building"]?.id,
        unitId:
          selectedEntities["unit"]?.resourceId ||
          selectedEntities["unit"]?.id,
        acquisitionId: itemId,
        isLoopScoped: Boolean(itemId),
      };
    }

    // Unknown collection type — fall back to top-level context, no scoping
    return {
      buildingId:
        selectedEntities["building"]?.resourceId ||
        selectedEntities["building"]?.id,
      unitId:
        selectedEntities["unit"]?.resourceId ||
        selectedEntities["unit"]?.id,
      acquisitionId:
        selectedEntities["acquisition"]?.resourceId ||
        selectedEntities["acquisition"]?.id,
      isLoopScoped: false,
    };
  };

  const isContextComplete = (): boolean => {
    const requiredInstances = getRequiredEntityInstances();
    const dynamicValuePlaceholders = getDynamicValuePlaceholders();

    // Check if all required entities are selected
    const entitiesComplete = requiredInstances.every(
      (instance) => selectedEntities[instance.fullEntity] !== undefined,
    );

    // Check if all dynamic values are filled
    const dynamicValuesComplete = dynamicValuePlaceholders.every(
      (placeholder) =>
        dynamicValues[placeholder.dynamicValueKey!] !== undefined &&
        dynamicValues[placeholder.dynamicValueKey!].trim() !== "",
    );

    // Image placeholders are optional: any {%imageN} without a selection is
    // stripped from the generated document by the backend.

    // Check if all loop dynamic values are filled
    // Loop-scoped images are optional (iteration might not have any pictures);
    // the backend strips unselected {%image} placeholders. Dynamic values
    // remain required.
    let loopDynamicValuesComplete = true;
    for (const loop of expandedLoops) {
      for (const item of loop.items) {
        for (const dynamicValue of item.dynamicValues) {
          const fieldKey = `${loop.loopVariable}_${item.index}_${dynamicValue}`;
          if (
            !loopDynamicValues[fieldKey] ||
            loopDynamicValues[fieldKey].trim() === ""
          ) {
            loopDynamicValuesComplete = false;
            break;
          }
        }
        if (!loopDynamicValuesComplete) break;
      }
      if (!loopDynamicValuesComplete) break;
    }

    return (
      entitiesComplete &&
      dynamicValuesComplete &&
      loopDynamicValuesComplete
    );
  };

  const handleGenerateDocument = async () => {
    if (!selectedTemplate || !isContextComplete()) {
      toast.error(
        "Selecteer eerst een sjabloon en vul alle benodigde context in",
      );
      return;
    }

    try {
      setEntitySearchLoading((prev) => ({ ...prev, generating: true }));

      // Prepare the context data for the backend
      const contextData: Record<string, any> = {};

      Object.entries(selectedEntities).forEach(
        ([entityInstance, entityData]) => {
          contextData[entityInstance] = {
            id: entityData.id,
            resourceId: entityData.resourceId,
            type: entityData.type,
            name: entityData.name,
            data: entityData,
          };
        },
      );

      console.log("Generating document with context:", contextData);

      // Merge dynamic values, image selections, loop dynamic values, and image table
      const allDynamicValues = {
        ...dynamicValues,
        ...imageSelections, // Add image SharePoint IDs to dynamic values
        ...loopDynamicValues, // Add loop-scoped dynamic values and images
        imagetableImages: imageTableSelections, // Add image table selections as array
      };

      console.log(
        "All dynamic values (including loop values):",
        allDynamicValues,
      );

      const response = await fetchWithAuth(
        `${API_URL}/document-generation/generate`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            templateId: selectedTemplate.id,
            context: contextData,
            placeholders: templatePlaceholders.filter((p) => p.isValid),
            dynamicValues: allDynamicValues, // Includes text, images, and image table
          }),
        },
        session,
      );

      if (response.ok) {
        // Get the file as a blob for download
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);

        // Create download link
        const link = document.createElement("a");
        link.href = url;
        link.download = `${selectedTemplate.name}_${new Date().toISOString().split("T")[0]}.docx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Clean up
        window.URL.revokeObjectURL(url);

        toast.success("Document succesvol gegenereerd en gedownload!");
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to generate document");
      }
    } catch (error) {
      console.error("Error generating document:", error);
      toast.error(
        "Kon document niet genereren: " +
          (error instanceof Error ? error.message : "Unknown error"),
      );
    } finally {
      setEntitySearchLoading((prev) => ({ ...prev, generating: false }));
    }
  };

  const categoryOptions = [
    { key: "ACQUISITION", label: "Acquisitie" },
    { key: "CONTRACT", label: "Contract" },
    { key: "INSPECTION", label: "Inspectie" },
    { key: "LEGAL", label: "Juridisch" },
    { key: "RENOVATION", label: "Renovatie" },
    { key: "GENERAL", label: "Algemeen" },
  ];

  const getCategoryLabel = (category: string): string => {
    return categoryOptions.find((opt) => opt.key === category)?.label ?? category;
  };

  const getUniqueCategories = (): string[] => {
    const categories = new Set<string>();
    templates.forEach((template) => {
      categories.add(template.category);
    });
    return Array.from(categories).sort();
  };

  const getFilteredTemplates = (): DocumentTemplate[] => {
    if (selectedCategory === "all") {
      return templates;
    }
    return templates.filter(
      (template) => template.category === selectedCategory,
    );
  };

  return (
    <div className="container mx-auto max-w-7xl p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="mb-2 flex items-center gap-3">
          <FileText size={32} className="text-primary" weight="duotone" />
          <h1 className="text-neutral-primaryDark text-3xl font-bold">
            Document Generator
          </h1>
        </div>
        <p className="text-neutral-secondaryMid">
          Genereer documenten met dynamische gegevens uit de database. Selecteer
          een sjabloon en vul de benodigde context in.
        </p>
      </div>

      {/* Prefilled Context Indicator */}
      {prefilledContext && (prefilledContext.entities && Object.keys(prefilledContext.entities).length > 0 ||
                            prefilledContext.dynamicValues && Object.keys(prefilledContext.dynamicValues).length > 0 ||
                            prefilledContext.category) && (
        <Card className="mb-6 border-2 border-moss-primary bg-moss-primary/5">
          <CardBody className="p-4">
            <div className="flex items-start gap-3">
              <CheckCircle size={24} className="text-moss-primary flex-shrink-0 mt-0.5" weight="duotone" />
              <div className="flex-1 space-y-3">
                <div>
                  <h3 className="text-base font-semibold text-moss-primary mb-1">
                    Context automatisch ingevuld
                  </h3>
                  <p className="text-sm text-neutral-secondary">
                    De volgende gegevens zijn meegegeven en automatisch ingevuld:
                  </p>
                </div>

                <div className="flex flex-wrap gap-2">
                  {prefilledContext.category && (
                    <Chip size="sm" color="success" variant="flat" startContent={<FileText size={14} />}>
                      Categorie: {getCategoryLabel(prefilledContext.category)}
                    </Chip>
                  )}
                  {prefilledContext.entities && Object.entries(prefilledContext.entities).map(([key, entity]: [string, any]) => (
                    <Chip key={key} size="sm" color="success" variant="flat" startContent={<Database size={14} />}>
                      {key}: {entity.name || entity.id || 'Geselecteerd'}
                    </Chip>
                  ))}
                  {prefilledContext.dynamicValues && Object.entries(prefilledContext.dynamicValues).map(([key, value]: [string, any]) => (
                    <Chip key={key} size="sm" color="success" variant="flat" startContent={<MagicWand size={14} />}>
                      {key}: {value}
                    </Chip>
                  ))}
                </div>
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Main Content */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Left Column - Template Selection */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <div className="flex w-full items-center justify-between">
                <div className="flex items-center gap-2">
                  <MagicWand size={20} className="text-primary" />
                  <h2 className="text-xl font-semibold">Document Sjablonen</h2>
                </div>
                <Chip variant="flat" color="primary">
                  {getFilteredTemplates().length} van {templates.length}{" "}
                  sjablonen
                </Chip>
              </div>
            </CardHeader>
            <CardBody className="space-y-4">
              {/* Category Filter */}
              <div className="flex items-center gap-4">
                <Select
                  label="Filter op categorie"
                  selectedKeys={[selectedCategory]}
                  onSelectionChange={(keys) => {
                    const selected = Array.from(keys)[0] as string;
                    setSelectedCategory(selected || "all");
                  }}
                  className="w-64"
                  size="sm"
                  items={[
                    { key: "all", label: "Alle categorieën" },
                    ...getUniqueCategories().map((cat) => ({
                      key: cat,
                      label:
                        categoryOptions.find((opt) => opt.key === cat)?.label ??
                        cat,
                    })),
                  ]}
                >
                  {(item) => (
                    <SelectItem key={item.key}>{item.label}</SelectItem>
                  )}
                </Select>
              </div>

              {/* Templates Table */}
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Spinner size="lg" />
                  <p className="text-neutral-secondaryMid ml-4">
                    Sjablonen laden...
                  </p>
                </div>
              ) : templates.length === 0 ? (
                <div className="py-12 text-center">
                  <FileText
                    size={64}
                    className="mx-auto mb-4 text-neutral-300"
                  />
                  <h3 className="text-neutral-primaryDark mb-2 text-lg font-medium">
                    Geen sjablonen beschikbaar
                  </h3>
                  <p className="text-neutral-secondaryMid mb-4">
                    Er zijn nog geen document sjablonen geconfigureerd. Ga naar
                    de instellingen om sjablonen toe te voegen.
                  </p>
                  <Button
                    color="primary"
                    variant="flat"
                    startContent={<MagicWand size={16} />}
                  >
                    Ga naar Instellingen
                  </Button>
                </div>
              ) : getFilteredTemplates().length === 0 ? (
                <div className="py-12 text-center">
                  <FileText
                    size={64}
                    className="mx-auto mb-4 text-neutral-300"
                  />
                  <p className="text-neutral-secondaryMid">
                    Geen sjablonen gevonden in de categorie "{selectedCategory}"
                  </p>
                </div>
              ) : (
                <Table aria-label="Document templates">
                  <TableHeader>
                    <TableColumn>NAAM</TableColumn>
                    <TableColumn>CATEGORIE</TableColumn>
                    <TableColumn>PLACEHOLDERS</TableColumn>
                    <TableColumn>LAATST GEWIJZIGD</TableColumn>
                    <TableColumn>ACTIES</TableColumn>
                  </TableHeader>
                  <TableBody>
                    {getFilteredTemplates().map((template) => (
                      <TableRow key={template.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{template.name}</p>
                            {template.description && (
                              <p className="text-neutral-secondaryMid text-sm">
                                {template.description}
                              </p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Chip size="sm" variant="flat" color="secondary">
                            {categoryOptions.find(
                              (c) => c.key === template.category,
                            )?.label ?? template.category}
                          </Chip>
                        </TableCell>
                        <TableCell>
                          <Chip size="sm" variant="flat" color="primary">
                            {template.placeholders?.length || 0} placeholders
                          </Chip>
                        </TableCell>
                        <TableCell>
                          <div className="text-neutral-secondaryMid flex items-center gap-1 text-sm">
                            <Calendar size={14} />
                            {new Date(template.updatedAt).toLocaleDateString(
                              "nl-NL",
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            variant="flat"
                            color="primary"
                            startContent={<Eye size={16} />}
                            onPress={() => handleTemplateSelect(template)}
                            isDisabled={selectedTemplate?.id === template.id}
                          >
                            {selectedTemplate?.id === template.id
                              ? "Geselecteerd"
                              : "Selecteren"}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardBody>
          </Card>
        </div>

        {/* Right Column - Context & Actions */}
        <div className="space-y-6">
          {/* Selected Template Info */}
          {selectedTemplate && (
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <FileText size={20} className="text-primary" />
                  <h3 className="text-lg font-semibold">
                    Geselecteerd Sjabloon
                  </h3>
                </div>
              </CardHeader>
              <CardBody className="space-y-4">
                <div>
                  <h4 className="text-neutral-primaryDark font-medium">
                    {selectedTemplate.name}
                  </h4>
                  {selectedTemplate.description && (
                    <p className="text-neutral-secondaryMid mt-1 text-sm">
                      {selectedTemplate.description}
                    </p>
                  )}
                </div>

                {/*Het volgende dat in commentaar staat was de placeholder analyse,
                hier is de werknemer redelijk weinig mee*/}

                {/*<Divider />*/}

                {/*<div>
                  <div className="mb-2 flex items-center justify-between">
                    <h5 className="text-neutral-primaryDark text-sm font-medium">
                      Placeholder Analyse:
                    </h5>
                    {templatePlaceholders.length > 0 && (
                      <div className="flex gap-1">
                        <Chip size="sm" color="success" variant="flat">
                          {templatePlaceholders.filter((p) => p.isValid).length}{" "}
                          geldig
                        </Chip>
                        {templatePlaceholders.filter((p) => !p.isValid).length >
                          0 && (
                          <Chip size="sm" color="danger" variant="flat">
                            {
                              templatePlaceholders.filter((p) => !p.isValid)
                                .length
                            }{" "}
                            ongeldig
                          </Chip>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="max-h-48 space-y-2 overflow-y-auto">
                    {templatePlaceholders.length === 0 ? (
                      <div className="flex items-center gap-2">
                        <Spinner size="sm" />
                        <p className="text-neutral-secondaryMid text-sm">
                          Placeholders analyseren...
                        </p>
                      </div>
                    ) : (
                      templatePlaceholders.map((placeholder, index) => (
                        <div
                          key={index}
                          className="rounded-lg border bg-neutral-50 p-2"
                        >
                          <div className="mb-1 flex items-center justify-between">
                            <code className="rounded bg-white px-2 py-1 font-mono text-xs">
                              {placeholder.placeholder}
                            </code>
                            <Chip
                              size="sm"
                              color={placeholder.isValid ? "success" : "danger"}
                              variant="flat"
                            >
                              {placeholder.isValid ? "Geldig" : "Ongeldig"}
                            </Chip>
                          </div>
                          {placeholder.isValid && (
                            <div className="text-neutral-secondaryMid text-xs">
                              <span className="font-medium">Entiteit:</span>{" "}
                              {placeholder.entity}
                              {placeholder.entityNumber && (
                                <span className="ml-1 rounded bg-blue-100 px-1 text-blue-700">
                                  #{placeholder.entityNumber}
                                </span>
                              )}
                              {placeholder.relationPath &&
                                placeholder.relationPath.length > 0 && (
                                  <span className="ml-1 rounded bg-green-100 px-1 text-xs text-green-700">
                                    → {placeholder.relationPath.join(" → ")}
                                  </span>
                                )}{" "}
                              •<span className="font-medium"> Eigenschap:</span>{" "}
                              {placeholder.property}
                              {placeholder.fullEntity &&
                                placeholder.fullEntity !==
                                  placeholder.entity && (
                                  <div className="mt-1 text-xs text-blue-600">
                                    Volledige referentie:{" "}
                                    {placeholder.fullEntity}
                                  </div>
                                )}
                              {placeholder.relationPath &&
                                placeholder.relationPath.length > 0 && (
                                  <div className="mt-1 text-xs text-green-600">
                                    Relatie keten: {placeholder.entity} →{" "}
                                    {placeholder.relationPath.join(" → ")} →{" "}
                                    {placeholder.property}
                                  </div>
                                )}
                            </div>
                          )}
                          {!placeholder.isValid && placeholder.errorMessage && (
                            <div className="mt-1 text-xs text-red-600">
                              {placeholder.errorMessage}
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>*/}

                {/* Dynamic Values Section */}
                {getDynamicValuePlaceholders().length > 0 && (
                  <>
                    <Divider />
                    <div>
                      <div className="mb-2 flex items-center justify-between">
                        <h5 className="text-neutral-primaryDark text-sm font-medium">
                          Dynamische Waarden:
                        </h5>
                        <Chip size="sm" color="primary" variant="flat">
                          {getDynamicValuePlaceholders().length} waarden
                        </Chip>
                      </div>

                      <div className="space-y-3">
                        {getDynamicValuePlaceholders().map(
                          (placeholder, index) => (
                            <div key={index} className="space-y-2">
                              <div className="flex items-center gap-2">
                                <code className="rounded bg-blue-50 px-2 py-1 font-mono text-xs text-blue-700">
                                  {placeholder.placeholder}
                                </code>
                                <Chip size="sm" color="primary" variant="flat">
                                  Dynamisch
                                </Chip>
                              </div>
                              <Textarea
                                size="sm"
                                placeholder={`Vul waarde in voor ${placeholder.dynamicValueKey}...`}
                                value={
                                  dynamicValues[placeholder.dynamicValueKey!] ||
                                  ""
                                }
                                onValueChange={(value) =>
                                  handleDynamicValueChange(
                                    placeholder.dynamicValueKey!,
                                    value,
                                  )
                                }
                                minRows={2}
                                maxRows={4}
                                className="text-sm"
                              />
                            </div>
                          ),
                        )}
                      </div>
                    </div>
                  </>
                )}

                {/* Image Table Section - Always available */}
                <Divider />
                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <h5 className="text-neutral-primaryDark text-sm font-medium">
                      Foto Tabel (optioneel):
                    </h5>
                    <Chip size="sm" color="secondary" variant="flat">
                      {imageTableSelections.length} foto's geselecteerd
                    </Chip>
                  </div>

                  <p className="text-neutral-secondaryMid mb-3 text-xs">
                    Voeg een tabel met foto's toe aan het einde van het document
                  </p>

                  <ImageTablePicker
                    selectedImageIds={imageTableSelections}
                    onImagesSelect={setImageTableSelections}
                    buildingId={
                      selectedEntities["building"]?.resourceId ||
                      selectedEntities["building"]?.id
                    }
                    unitId={
                      selectedEntities["unit"]?.resourceId ||
                      selectedEntities["unit"]?.id
                    }
                    acquisitionId={
                      selectedEntities["acquisition"]?.resourceId ||
                      selectedEntities["acquisition"]?.id
                    }
                  />
                </div>

                {/*Dit stukje liet zien welke entiteiten er waren gevonden en nodig waren,
                dit is niet echt meer nodig en was eerder voor debuging*/}

                {/*{getRequiredEntityInstances().length > 0 && (
                  <>
                    <Divider />
                    <div>
                      <h5 className="text-neutral-primaryDark mb-2 text-sm font-medium">
                        Benodigde Entiteiten:
                      </h5>
                      <div className="flex flex-wrap gap-1">
                        {getRequiredEntityInstances().map((instance) => (
                          <Chip
                            key={instance.fullEntity}
                            size="sm"
                            color="secondary"
                            variant="flat"
                          >
                            {instance.displayName}
                          </Chip>
                        ))}
                      </div>
                    </div>
                  </>
                )}*/}
              </CardBody>
            </Card>
          )}

          {/* Context Selection */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Database size={20} className="text-secondary" />
                <h3 className="text-lg font-semibold">Context</h3>
              </div>
            </CardHeader>
            <CardBody className="space-y-4">
              {!selectedTemplate ? (
                <div className="text-neutral-secondaryMid text-sm">
                  <p>
                    Selecteer eerst een sjabloon om de benodigde context te
                    zien.
                  </p>
                </div>
              ) : getRequiredEntityInstances().length === 0 ? (
                <div className="text-neutral-secondaryMid text-sm">
                  <p>Dit sjabloon vereist geen specifieke context.</p>
                </div>
              ) : (
                <>
                  <div className="text-neutral-secondaryMid mb-4 text-sm">
                    <p>Selecteer de context voor document generatie:</p>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <Chip
                        size="sm"
                        color={isContextComplete() ? "success" : "warning"}
                        variant="flat"
                      >
                        {Object.keys(selectedEntities).length} van{" "}
                        {getRequiredEntityInstances().length} entiteiten
                      </Chip>
                      {getDynamicValuePlaceholders().length > 0 && (
                        <Chip
                          size="sm"
                          color={
                            getDynamicValuePlaceholders().every((p) =>
                              dynamicValues[p.dynamicValueKey!]?.trim(),
                            )
                              ? "success"
                              : "warning"
                          }
                          variant="flat"
                        >
                          {
                            Object.keys(dynamicValues).filter((k) =>
                              dynamicValues[k]?.trim(),
                            ).length
                          }{" "}
                          van {getDynamicValuePlaceholders().length} waarden
                        </Chip>
                      )}
                      {imagePlaceholders.length > 0 && (
                        <Chip
                          size="sm"
                          color={
                            imagePlaceholders.every((img) =>
                              imageSelections[img]?.trim(),
                            )
                              ? "success"
                              : "default"
                          }
                          variant="flat"
                        >
                          {
                            Object.keys(imageSelections).filter((k) =>
                              imageSelections[k]?.trim(),
                            ).length
                          }{" "}
                          van {imagePlaceholders.length} afbeeldingen
                        </Chip>
                      )}
                    </div>
                  </div>

                  <div className="space-y-4">
                    {getRequiredEntityInstances().map((instance) => (
                      <EntitySearchBar
                        key={instance.fullEntity}
                        entityType={instance.baseEntity}
                        entityInstance={instance.fullEntity}
                        displayName={instance.displayName}
                        selectedEntity={selectedEntities[instance.fullEntity]}
                        onEntitySelect={(entity) =>
                          handleEntitySelect(instance.fullEntity, entity)
                        }
                        onEntityClear={() =>
                          handleEntityClear(instance.fullEntity)
                        }
                        isLoading={
                          entitySearchLoading[instance.fullEntity] || false
                        }
                      />
                    ))}
                  </div>
                </>
              )}
            </CardBody>
          </Card>

          {/* Image Selection Card */}
          {imagePlaceholders.length > 0 && (
            <Card>
              <CardHeader>
                <div className="flex w-full items-center justify-between">
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-semibold">Afbeeldingen</h3>
                  </div>
                  <div className="flex items-center gap-2">
                    <Chip size="sm" color="secondary" variant="flat">
                      {imagePlaceholders.length} afbeelding(en)
                    </Chip>
                    <Chip
                      size="sm"
                      color={
                        imagePlaceholders.every((n) =>
                          imageSelections[n]?.trim(),
                        )
                          ? "success"
                          : "default"
                      }
                      variant="flat"
                    >
                      {
                        Object.keys(imageSelections).filter((k) =>
                          imageSelections[k]?.trim(),
                        ).length
                      }{" "}
                      geselecteerd
                    </Chip>
                  </div>
                </div>
              </CardHeader>

              <CardBody className="space-y-4">
                <p className="text-neutral-secondary-mid text-sm">
                  Afbeeldingen zijn optioneel. Plaatshouders zonder selectie
                  worden automatisch uit het gegenereerde document verwijderd.
                </p>
                {imagePlaceholders.map((imageName, index) => (
                  <ImagePicker
                    key={index}
                    imageName={imageName}
                    selectedImageId={imageSelections[imageName]}
                    onImageSelect={(sharePointFileId) =>
                      handleImageSelect(imageName, sharePointFileId)
                    }
                    buildingId={
                      selectedEntities["building"]?.resourceId ||
                      selectedEntities["building"]?.id
                    }
                    unitId={
                      selectedEntities["unit"]?.resourceId ||
                      selectedEntities["unit"]?.id
                    }
                    acquisitionId={
                      selectedEntities["acquisition"]?.resourceId ||
                      selectedEntities["acquisition"]?.id
                    }
                    prefilledBuilding={selectedEntities["building"]}
                  />
                ))}
              </CardBody>
            </Card>
          )}

          {/* Loop-Specific Dynamic Fields */}
          {expandedLoops.length > 0 && (
            <Card>
              <CardHeader>
                <div className="flex w-full items-center justify-between">
                  <div className="flex items-center gap-2">
                    <MagicWand size={20} className="text-purple-600" />
                    <h3 className="text-lg font-semibold">Loop Velden</h3>
                  </div>
                  <Chip size="sm" color="secondary" variant="flat">
                    {expandedLoops.reduce(
                      (sum, loop) => sum + loop.items.length,
                      0,
                    )}{" "}
                    items
                  </Chip>
                </div>
              </CardHeader>

              <CardBody className="space-y-6">
                {expandedLoops.map((loop, loopIndex) => (
                  <div key={loopIndex} className="space-y-4">
                    <div className="rounded-lg bg-purple-50 p-3">
                      <h4 className="text-sm font-semibold text-purple-900">
                        {loop.loopVariable} in {loop.collectionPath}
                      </h4>
                      <p className="text-xs text-purple-700">
                        {loop.items.length} item(s)
                      </p>
                    </div>

                    {loop.items.map((item, itemIndex) => (
                      <div
                        key={itemIndex}
                        className="border-neutral-stroke bg-neutral-bgDefault space-y-3 rounded-lg border p-4"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex flex-col">
                            <h5 className="text-neutral-primaryDark text-sm font-semibold">
                              {item.itemLabel}
                            </h5>
                            {item.itemData?.id != null &&
                              String(item.itemData.id) !==
                                String(item.itemLabel) && (
                                <code className="text-neutral-secondary-mid font-mono text-xs">
                                  {String(item.itemData.id)}
                                </code>
                              )}
                          </div>
                          <Chip size="sm" variant="flat" color="default">
                            #{item.index + 1}
                          </Chip>
                        </div>

                        {/* Images for this item */}
                        {item.images.length > 0 && (
                          <div className="space-y-2">
                            <p className="text-neutral-secondaryDark text-xs font-medium">
                              Afbeeldingen:
                            </p>
                            {item.images.map((imageName, imgIndex) => {
                              const fieldKey = `${loop.loopVariable}_${item.index}_${imageName}`;
                              const pickerContext =
                                getLoopItemImagePickerContext(loop, item);
                              const itemIdString =
                                item.itemData?.id != null
                                  ? String(item.itemData.id)
                                  : "";
                              const displayItemLabel =
                                itemIdString &&
                                itemIdString !== String(item.itemLabel)
                                  ? `${item.itemLabel} (${itemIdString})`
                                  : item.itemLabel;
                              return (
                                <ImagePicker
                                  key={imgIndex}
                                  imageName={`${displayItemLabel} - ${imageName}`}
                                  selectedImageId={loopDynamicValues[fieldKey]}
                                  onImageSelect={(sharePointFileId) => {
                                    setLoopDynamicValues((prev) => ({
                                      ...prev,
                                      [fieldKey]: sharePointFileId,
                                    }));
                                  }}
                                  buildingId={pickerContext.buildingId}
                                  unitId={pickerContext.unitId}
                                  acquisitionId={pickerContext.acquisitionId}
                                  isLoopScoped={pickerContext.isLoopScoped}
                                />
                              );
                            })}
                          </div>
                        )}

                        {/* Dynamic values for this item */}
                        {item.dynamicValues.length > 0 && (
                          <div className="space-y-2">
                            <p className="text-neutral-secondaryDark text-xs font-medium">
                              Dynamische waarden:
                            </p>
                            {item.dynamicValues.map((dynamicValue, dvIndex) => {
                              const fieldKey = `${loop.loopVariable}_${item.index}_${dynamicValue}`;
                              const displayName = dynamicValue.replace(
                                "waarde.",
                                "",
                              );
                              return (
                                <div key={dvIndex} className="space-y-2">
                                  <div className="flex items-center gap-2">
                                    <code className="rounded bg-blue-50 px-2 py-1 font-mono text-xs text-blue-700">
                                      {dynamicValue}
                                    </code>
                                  </div>
                                  <Textarea
                                    label={`${item.itemLabel} - ${displayName}`}
                                    placeholder={`Voer waarde in voor ${displayName}...`}
                                    value={loopDynamicValues[fieldKey] || ""}
                                    onChange={(e) => {
                                      setLoopDynamicValues((prev) => ({
                                        ...prev,
                                        [fieldKey]: e.target.value,
                                      }));
                                    }}
                                    size="sm"
                                    variant="bordered"
                                  />
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ))}
              </CardBody>
            </Card>
          )}

          {/* Generation Actions */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Download size={20} className="text-success" />
                <h3 className="text-lg font-semibold">Genereren</h3>
              </div>
            </CardHeader>
            <CardBody className="space-y-4">
              <div className="text-neutral-secondaryMid text-sm">
                <p>Genereer het document met de geselecteerde gegevens.</p>
              </div>

              <Divider />

              <div className="space-y-3">
                <Button
                  color="primary"
                  className="w-full"
                  startContent={<Download size={16} />}
                  isDisabled={!selectedTemplate || !isContextComplete()}
                  isLoading={entitySearchLoading.generating}
                  onPress={handleGenerateDocument}
                >
                  Genereer Document
                </Button>
              </div>

              <div className="text-neutral-secondaryMid text-xs">
                {!selectedTemplate ? (
                  <p>
                    <strong>Stap 1:</strong> Selecteer een sjabloon om te
                    beginnen.
                  </p>
                ) : !isContextComplete() ? (
                  <p>
                    <strong>Stap 2:</strong> Vul alle benodigde context en
                    dynamische waarden in om het document te kunnen genereren.
                  </p>
                ) : (
                  <p>
                    <strong>Klaar!</strong> Je kunt nu het document genereren.
                  </p>
                )}
              </div>
            </CardBody>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default DocumentGenerator;
